import { useEffect, useMemo, useState } from 'react'

import { fetchPhotoObjectUrl, revokePhotoObjectUrl } from '../utilities/photo-url'

/**
 * Loads a single photo via authenticated fetch into a blob object URL.
 * Revokes the object URL on unmount or when photoId/size changes.
 */
export function usePhotoObjectUrl(photoId: string | null | undefined, size?: number) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!photoId) {
      setUrl(null)
      return
    }

    let cancelled = false
    let objectUrl: string | null = null

    fetchPhotoObjectUrl(photoId, size).then((next) => {
      if (cancelled) {
        revokePhotoObjectUrl(next)
        return
      }
      objectUrl = next
      setUrl(next)
    })

    return () => {
      cancelled = true
      revokePhotoObjectUrl(objectUrl)
    }
  }, [photoId, size])

  return url
}

/**
 * Loads many photos; returns URLs in the same order as photoIds (nulls for failures).
 */
export function usePhotoObjectUrls(photoIds: readonly string[], size?: number) {
  const idsKey = useMemo(() => photoIds.join(','), [photoIds])
  const [urls, setUrls] = useState<(string | null)[]>(() => photoIds.map(() => null))

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : []
    if (ids.length === 0 || (ids.length === 1 && ids[0] === '')) {
      setUrls([])
      return
    }

    let cancelled = false
    const created: string[] = []

    setUrls(ids.map(() => null))

    Promise.all(
      ids.map(async (id) => {
        const objectUrl = await fetchPhotoObjectUrl(id, size)
        if (objectUrl) created.push(objectUrl)
        return objectUrl
      }),
    ).then((next) => {
      if (cancelled) {
        for (const url of created) revokePhotoObjectUrl(url)
        return
      }
      setUrls(next)
    })

    return () => {
      cancelled = true
      for (const url of created) revokePhotoObjectUrl(url)
    }
  }, [idsKey, size])

  return urls
}
